import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TicketCard } from "@/components/TicketCard";
import { fmtMoney } from "@/components/ticketMeta";
import { api, formatApiError } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl border bg-card p-4">
    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="mt-1 font-display text-2xl font-bold" data-testid={testId}>{value}</div>
  </div>
);

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${id}`).then((r) => { setData(r.data); setForm(r.data.client); })
      .catch(() => { toast.error("Clientul nu a fost găsit"); nav("/clienti"); });
  }, [id, nav]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { name, phone, address, notes } = form;
      await api.put(`/clients/${id}`, { name, phone, address, notes });
      toast.success("Client actualizat");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!data || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const { client: c, tickets } = data;

  return (
    <div data-testid="client-detail-view" className="animate-in fade-in duration-300">
      <button onClick={() => nav("/clienti")} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="client-back-button"><ArrowLeft className="h-4 w-4" /> Clienți</button>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="client-detail-name">{c.name}</h1>
        <Button asChild data-testid="client-new-ticket-button"><Link to={`/nou?client=${c.id}`}><Plus className="h-4 w-4" /> Tichet nou pentru client</Link></Button>
      </div>
      <div className="mb-6 grid grid-cols-3 gap-3 sm:gap-4">
        <Stat label="Intervenții" value={c.tickets_count} testId="client-tickets-count" />
        <Stat label="Active" value={c.open_count} testId="client-open-count" />
        <Stat label="Total încasat" value={fmtMoney(c.total_cost)} testId="client-total-cost" />
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-xl border bg-card p-5 sm:p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-primary">Date client</h3>
          <div className="space-y-1.5"><Label>Nume</Label><Input value={form.name} onChange={set("name")} data-testid="client-name-input" /></div>
          <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={set("phone")} data-testid="client-phone-input" /></div>
          <div className="space-y-1.5"><Label>Adresă</Label><Input value={form.address} onChange={set("address")} data-testid="client-address-input" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="client-notes-input" /></div>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="client-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salvează
          </Button>
        </div>
        <div className="lg:col-span-2">
          <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Istoric tichete</h3>
          {tickets.length === 0 ? (
            <p className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">Niciun tichet.</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="client-tickets-list">
              {tickets.map((t, i) => <TicketCard key={t.id} t={t} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
